import { useContext } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { UserContext } from '../contexts/UserContext'
import {
  checkInvite,
  checkPwReset,
  confirmEmailChallenge,
  confirmOtp,
  disableUser,
  enrollAuthenticator,
  getAuthenticators,
  registerWithInvite,
  resetMFA,
  resetPw,
  sendPwResetEmail,
  triggerChallenge,
} from '../api/auth'
import { getProfileDetails, postUpdate, postAvatar, putProfileNotifications } from '../api/react/settings/Profile'

export const useAuth = () => {
  const context = useContext(UserContext)
  if (!context) throw new Error('useAuth must be used within UserProvider')
  return context
}

export const useCheckInvite = (token: string | null) => {
  return useQuery({
    queryKey: ['invite', token],
    queryFn: () => checkInvite(token ?? ''),
    enabled: !!token,
    retry: 1,
  })
}

export const useRegisterWithInvite = () => {
  return useMutation({
    mutationFn: registerWithInvite,
  })
}

export const useSendPwResetEmail = () => {
  return useMutation({
    mutationFn: sendPwResetEmail,
  })
}

export const useCheckPwReset = (token: string | null) => {
  return useQuery({
    queryKey: ['checkPwToken', token],
    queryFn: () => checkPwReset(token ?? ''),
    enabled: !!token,
    retry: 1,
  })
}

export const useResetPw = () => {
  return useMutation({
    mutationFn: resetPw,
  })
}

export const useResetMFA = () => {
  return useMutation({
    mutationFn: resetMFA,
  })
}

export const useDisableUser = () => {
  return useMutation({
    mutationFn: disableUser,
  })
}

export const PROFILE_KEYS = {
  profile: () => ['userProfile'] as const,
}

export const useProfileDetails = () => {
  return useQuery({
    queryKey: PROFILE_KEYS.profile(),
    queryFn: getProfileDetails,
  })
}

export const useUpdateProfile = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: postUpdate,
    onSuccess: () => qc.invalidateQueries({ queryKey: PROFILE_KEYS.profile() }),
  })
}

export const useUpdateAvatar = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: postAvatar,
    onSuccess: () => qc.invalidateQueries({ queryKey: PROFILE_KEYS.profile() }),
  })
}

export const useUpdateProfileNotifications = () => {
  return useMutation({
    mutationFn: putProfileNotifications,
  })
}

// MFA hooks

export const useGetAuthenticators = (mfaToken: string | null) => {
  return useQuery({
    queryKey: ['mfa-authenticators', mfaToken],
    queryFn: () => getAuthenticators(mfaToken!),
    enabled: !!mfaToken,
    retry: 0,
    refetchOnWindowFocus: false,
  })
}

export const useTriggerChallenge = () => {
  return useMutation({ mutationFn: triggerChallenge })
}

export const useConfirmOtp = () => {
  return useMutation({ mutationFn: confirmOtp })
}

export const useConfirmEmail = () => {
  return useMutation({ mutationFn: confirmEmailChallenge })
}

export const useEnrollAuthenticator = () => {
  return useMutation({ mutationFn: enrollAuthenticator })
}
